import { ApplicationConfig, inject, PLATFORM_ID, provideZoneChangeDetection, provideAppInitializer } from '@angular/core';
import { PreloadAllModules, provideRouter, RouterLinkWithHref, RouterModule, RouterOutlet, withPreloading, withRouterConfig } from '@angular/router';
import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { importProvidersFrom, isDevMode } from '@angular/core';
import { withInterceptorsFromDi, provideHttpClient, HttpClient, withFetch } from '@angular/common/http';
import { TranslateStore, TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { DatePipe, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { BrowserModule } from '@angular/platform-browser';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CsrfProvider } from './core/interceptors/csrf-provider';
import { AuthProvider } from './core/interceptors/auth-provider';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ColorThemeReducers } from './store/color-theme/reducers';
import { ActiveLanguageReducers } from './store/language/reducers';
import { JobsReducers } from './store/jobs/reducers';
import { JobsDataEffect } from './store/jobs/effects';
import { provideAnimations } from '@angular/platform-browser/animations';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes,
      withPreloading(PreloadAllModules),
      // withScrollPositionRestoration('enabled'),
      // withRouterConfig({ anchorScrolling: 'enabled' }) // optional for #anchors
    ),
    provideState("colorTheme", ColorThemeReducers),
    provideState("activeLanguage", ActiveLanguageReducers),
    provideStore({
      colorTheme: ColorThemeReducers,
      activeLanguage: ActiveLanguageReducers,
      jobsData: JobsReducers
      }),
    CsrfProvider,
    AuthProvider,
    provideEffects([JobsDataEffect]),
    importProvidersFrom(
      BrowserModule,
      RouterModule,
      RouterOutlet,
      RouterLinkWithHref,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient, DOCUMENT, PLATFORM_ID],
        },
      }),
      StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: !isDevMode(), // Restrict extension to log-only mode
        autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        trace: true, // If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
        traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
        connectInZone: true, // If set to true, the connection is established within the Angular zone
      })
    ),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const document = inject(DOCUMENT);
      const platformId = inject(PLATFORM_ID);
      const initializerFn = (languageInitializer)(translate,document,platformId);
      return initializerFn();
      }),
    CsrfProvider,
    AuthProvider,
    TranslateStore,
    DatePipe,
    CookieService,
    provideHttpClient(
    withInterceptorsFromDi(),
    withFetch()),
    provideClientHydration(withEventReplay()),
    provideAnimations(),
  ]
};

function httpLoaderFactory(_http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(
    _http,
    './assets/i18n/page/page-',
    '.json',
  );
}

export function languageInitializer(
  translate: TranslateService,
  document: Document,
  platformId: Object) {
  return () => {
    if (isPlatformBrowser(platformId)) {
      let selectedLanguage: string = 'en'
      const UrlFragments = window.location.pathname.split('/'); // Extract language from URL
      const languageInUrl = ['en', 'de', 'fa', 'id'].some(value => UrlFragments.includes(value));
      if(!languageInUrl) {
        const localStorageLanguage = localStorage.getItem('active-language')
        //if no value is saved in the localStorage.
        if(!localStorageLanguage){
          selectedLanguage = 'en'
          localStorage.setItem('active-language', 'en')
        } else {
          selectedLanguage = localStorageLanguage
        }
      } else {
        let languageInURL = UrlFragments[1]
        localStorage.setItem('active-language', languageInURL)
        selectedLanguage = languageInURL
      }
      document.documentElement.lang = selectedLanguage; // Set <html lang="...">
      translate.setDefaultLang(selectedLanguage);
      return translate.use(selectedLanguage).toPromise(); // Load language translations
    }
    translate.setDefaultLang('en');
    return translate.use('en').toPromise(); // Load language translations
  };
}
function withScrollPositionRestoration(arg0: string): import("@angular/router").RouterFeatures {
  throw new Error('Function not implemented.');
}

